import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';

import withMessages, { FormattedHTMLMessage } from 'component-utilities/intl';
import naturalCompare from 'natural-compare-lite';

import highlightText from 'v-c/Text/HighlightText';
import {
    TableView,
    TableHeader,
    TableBody,
    TableRow,
    CheckboxCell,
    EmptyCell,
    TextCell,
} from 'v-c/TableView';
import SortCell, { sortKeys } from 'v-c/TableView/SortCell';
import { ActionButtonsContainer } from 'v-c/Buttons/ActionButtons';
import { Findtool } from 'v-c/Buttons/ActionButtons/Actions';

import { formatTZDate } from 'component-utilities/dates/dates';
import displayName from 'component-lib/displayName';

import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import ModalContentCenter from '../ModalContentCenter';
import ModalButtons from '../ModalButtons';

const { UNSORTED, DESC } = sortKeys;
const BASE_KEY = 'components.modals.shared.saved_lists';
const COLUMNS = ['name', 'lastModifiedTime'];
const COLUMN_FILTER = {
    lastModifiedTime: formatTZDate,
};
const COLUMN_SORT_COMPARES = {
    name: (a, b) => naturalCompare(a.toLowerCase(), b.toLowerCase()),
    lastModifiedTime: (a, b) => a.format('x') - b.format('x'),
};

@withMessages
export default class SavedLists extends Component {
    static displayName = displayName('Modals/Shared/SavedLists');

    static propTypes = {
        items: PropTypes.array.isRequired,
        onCloseClick: PropTypes.func.isRequired,
        onClickCheckItem: PropTypes.func.isRequired,
        onPrimaryClick: PropTypes.func.isRequired,
        onSecondaryClick: PropTypes.func.isRequired,
        getMessage: PropTypes.func.isRequired,
        selectedItems: PropTypes.array.isRequired,
        originalSelectedItems: PropTypes.array.isRequired,
    };

    constructor(props) {
        super(props);
        this.state = { currentNameFilter: '', sortOrder: {} };
    }

    setSortOrder = (cellId, newSortDirection) => {
        this.setState({ sortOrder: { cellId, sortKey: newSortDirection } });
    };

    get hasSelectedItems() {
        return !isEqual(this.props.originalSelectedItems, this.props.selectedItems);
    }

    get visibleItems() {
        const visibleItems = this.props.items.filter(item => COLUMNS.some(which => (COLUMN_FILTER[which] ? COLUMN_FILTER[which](item[which]) : item[which])
            .toLowerCase()
            .indexOf(this.state.currentNameFilter.toLowerCase()) !== -1,),);

        const { cellId, sortKey } = this.state.sortOrder;

        if (cellId) {
            if (sortKey === UNSORTED) {
                return visibleItems;
            }

            visibleItems.sort((a, b) => COLUMN_SORT_COMPARES[cellId](a[cellId], b[cellId]));

            if (sortKey === DESC) {
                visibleItems.reverse();
            }
        }

        return visibleItems;
    }

    clearUpdateNameFilter = () => {
        this.setState({ currentNameFilter: '' });
    };

    handleUpdateNameFilter = (e) => {
        this.setState({ currentNameFilter: e.target.value });
    };

    handlePrimaryClick = () => {
        if (!this.hasSelectedItems) {
            return;
        }

        this.props.onPrimaryClick();
    };

    render() {
        const {
            items,
            getMessage,
            onCloseClick,
            onSecondaryClick,
            onClickCheckItem,
            selectedItems,
        } = this.props;

        return (
            <ModalContainer>
                <ModalHeader onCloseClick={onCloseClick}>
                    <FormattedHTMLMessage id={`${BASE_KEY}.title`} values={{ count: items.length }} />
                </ModalHeader>
                <ModalContentCenter>
                    <ActionButtonsContainer>
                        <Findtool
                            placeholder={getMessage(`${BASE_KEY}.placeholder`)}
                            value={this.state.currentNameFilter}
                            onChange={this.handleUpdateNameFilter}
                            onClose={this.clearUpdateNameFilter}
                        />
                    </ActionButtonsContainer>
                    <TableView tableId="saved-lists">
                        <TableHeader>
                            <TableRow index={0} rowId="header">
                                <EmptyCell cellId="selected" key="selected" type="name" />
                                {COLUMNS.map(which => (
                                    <SortCell
                                        cellId={which}
                                        sortKey={
                                            this.state.sortOrder.cellId === which
                                                ? this.state.sortOrder.sortKey
                                                : UNSORTED
                                        }
                                        onClick={this.setSortOrder}
                                        key={which}
                                        isFloatingCell={which === 'name'}
                                        type="name"
                                        text={getMessage(`${BASE_KEY}.table.${which}`)}
                                    />
                                ))}
                            </TableRow>
                        </TableHeader>
                    </TableView>
                    <div style={{ overflowY: 'scroll', maxHeight: '200px' }}>
                        <TableView tableId="saved-list-content">
                            <TableBody>
                                {this.visibleItems.map((item, index) => (
                                    <TableRow index={index} rowId={String(item.id)} key={item.id}>
                                        <CheckboxCell
                                            cellId="selected"
                                            isChecked={selectedItems.indexOf(item.id) !== -1}
                                            onClick={onClickCheckItem(item.id)}
                                        />
                                        {COLUMNS.map(which => (
                                            <TextCell
                                                key={which}
                                                type="name"
                                                isFloatingCell={which === 'name'}
                                                cellId={which}
                                            >
                                                <div style={{ textAlign: 'left' }}>
                                                    {highlightText(
                                                        COLUMN_FILTER[which] ? COLUMN_FILTER[which](item[which]) : item[which],
                                                        [this.state.currentNameFilter.toLowerCase()],
                                                    )}
                                                </div>
                                            </TextCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </TableView>
                    </div>
                </ModalContentCenter>
                <ModalButtons
                    primaryText={getMessage(`${BASE_KEY}.buttons.primary`)}
                    secondaryText={getMessage(`${BASE_KEY}.buttons.secondary`)}
                    primaryDisabled={!this.hasSelectedItems}
                    onPrimaryClick={this.handlePrimaryClick}
                    onSecondaryClick={onSecondaryClick}
                />
            </ModalContainer>
        );
    }
}
